import { Alert, Box, Button, Fade, Grid, IconButton, MenuItem, Paper, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { convertUnit, formatNumber, getUnitSymbol, getUnitsForCategory } from '../utils/converter';

import SwapVertIcon from '@mui/icons-material/SwapVert';
import { parseConversionQuery } from '../utils/nlp';
import { useTranslation } from 'react-i18next';

function UnitConverter({ category, nlInput: nlInputProp = '', onNlInputChange }) {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const [fromUnit, setFromUnit] = useState('');
  const [toUnit, setToUnit] = useState('');
  const [fromValue, setFromValue] = useState('1');
  const [toValue, setToValue] = useState('');
  const [nlInput, setNlInput] = useState('');
  const [nlMessage, setNlMessage] = useState('');

  // Get units for the current category
  const units = React.useMemo(() => getUnitsForCategory(category, i18n.language), [category, i18n.language]);

  // Example helpers per category (fallbacks)
  const categoryExamples = {
    length: '100 meters to feet',
    mass: '100 kilograms to grams',
    area: '100 square meters to square feet',
    volume: '1 liter to milliliters',
    temperature: '100 celsius to fahrenheit',
    time: '1 hour to minutes',
    energy: '1 joule to kilojoules',
    power: '1 watt to horsepower',
    pressure: '1 atm to pascal',
    angle: '180 degrees to radians',
  };

  // Helper to replace common superscript characters with digits for examples
  const flattenSuperscripts = s => String(s).replace(/\u00b2/g, '2').replace(/\u00b3/g, '3');

  // Initialize units when category changes
  useEffect(() => {
    if (units.length > 0) {
      const firstUnit = units[0];
      const secondUnit = units.length > 1 ? units[1] : units[0];
      setFromUnit(firstUnit);
      setToUnit(secondUnit);
      setFromValue('1');
    }
  }, [category, units]);

  // Perform conversion whenever any input changes
  useEffect(() => {
    if (fromUnit && toUnit && fromValue !== '') {
      const numValue = parseFloat(fromValue);
      if (!isNaN(numValue)) {
        const result = convertUnit(numValue, fromUnit, toUnit, category);
        const formattedResult = formatNumber(result);
        setToValue(formattedResult);
      } else {
        setToValue('');
      }
    } else if (fromValue === '') {
      setToValue('');
    }
  }, [fromValue, fromUnit, toUnit, category]);

  // Handle natural-language input changes
  useEffect(() => {
    if (!nlInput || nlInput.trim() === '') {
      setNlMessage('');
      return;
    }

    const parsed = parseConversionQuery(nlInput);
    if (!parsed) {
      setNlMessage('Could not understand the query. Try "100 square feet to square meters"');
      return;
    }

    const { value, fromUnit: pFrom, toUnit: pTo, category: pCat } = parsed;

    // If parsed category differs, suggest switching but do not auto-switch
    if (pCat !== category) {
      setNlMessage(`Parsed units belong to ${pCat}. Select that category or type units within the current category.`);
      return;
    }

    // Run conversion and populate the UI
    const result = convertUnit(value, pFrom, pTo, pCat);
    const formatted = formatNumber(result);

    setFromUnit(pFrom);
    setToUnit(pTo);
    setFromValue(String(value));
    setToValue(formatted);
    setNlMessage(`Parsed: ${value} ${pFrom} → ${formatted} ${pTo}`);
  }, [nlInput, category]);

  // Sync prop -> local state when category changes or prop changes
  useEffect(() => {
    setNlInput(nlInputProp || '');
  }, [nlInputProp, category]);

  // on change, if parent handler exists, update parent storage as well
  const handleNlInputChange = val => {
    setNlInput(val);
    if (typeof onNlInputChange === 'function') onNlInputChange(category, val);
  };

  const handleFromValueChange = e => {
    const value = e.target.value;
    // Allow empty, numbers, decimal point, minus sign, and scientific notation
    if (value === '' || /^-?\d*\.?\d*([eE][-+]?\d*)?$/.test(value)) {
      setFromValue(value);
    }
  };

  const handleFromUnitChange = e => {
    const newUnit = e.target.value;

    if (newUnit && newUnit !== fromUnit) {
      setFromUnit(newUnit);
    }
  };

  const handleToUnitChange = e => {
    const newUnit = e.target.value;

    if (newUnit && newUnit !== toUnit) {
      setToUnit(newUnit);
    }
  };

  const handleSwapUnits = () => {
    // Swap both units and values
    const oldFromUnit = fromUnit;
    const oldToUnit = toUnit;

    // Also swap the numeric values: put the current toValue into fromValue and vice-versa.
    // toValue may be formatted (string), so keep it as-is. fromValue can be empty string.
    const oldFromValue = fromValue;
    const oldToValue = toValue;

    setFromUnit(oldToUnit);
    setToUnit(oldFromUnit);
    setFromValue(oldToValue);
    setToValue(oldFromValue);
  };

  if (!category || units.length === 0) {
    return null;
  }

  return (
    <Fade in timeout={400}>
      <Box sx={{ maxWidth: 700, mx: 'auto' }}>
        <Paper
          elevation={10}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 4,
            position: 'relative',
          }}
        >
          {/* From Section */}
          {/* Natural language input */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>
              Natural language (try typing a conversion)
            </Typography>
            <TextField
              fullWidth
              variant="filled"
              value={nlInput}
              onChange={e => handleNlInputChange(e.target.value)}
              placeholder={
                // prefer using the first two units from the category to build an accurate example
                units && units.length >= 2
                  ? `e.g. 100 ${t(`units.${units[0]}`)} to ${t(`units.${units[1]}`)}`
                  : categoryExamples[category] || 'e.g. 100 square feet to square meters'
              }
              InputProps={{
                sx: { bgcolor: '#fff' },
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Build two example formats: short and question */}
              {units &&
                units.length >= 2 &&
                (() => {
                  const short = `100 ${flattenSuperscripts(
                    getUnitSymbol(category, units[0]) || t(`units.${units[0]}`)
                  )} to ${flattenSuperscripts(getUnitSymbol(category, units[1]) || t(`units.${units[1]}`))}`;
                  const question = `How many ${flattenSuperscripts(
                    getUnitSymbol(category, units[1]) || t(`units.${units[1]}`)
                  )} in 100 ${flattenSuperscripts(getUnitSymbol(category, units[0]) || t(`units.${units[0]}`))}?`;
                  return (
                    <>
                      <Button size="small" variant="outlined" onClick={() => handleNlInputChange(short)}>
                        {short}
                      </Button>
                      <Button size="small" variant="text" onClick={() => handleNlInputChange(question)}>
                        {question}
                      </Button>
                    </>
                  );
                })()}

              {nlMessage && (
                <Typography variant="caption" color="text.secondary" mt={1} display="block">
                  {nlMessage}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" fontWeight={600} color="text.secondary" mb={1.5}>
              {t('from')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={fromValue}
                  onChange={handleFromValueChange}
                  placeholder={t('enterValue')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '1.125rem',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={7}>
                <TextField
                  fullWidth
                  select
                  variant="outlined"
                  value={fromUnit}
                  onChange={handleFromUnitChange}
                  SelectProps={{
                    native: false,
                  }}
                >
                  {units.map(unit => (
                    <MenuItem key={unit} value={unit}>
                      {t(`units.${unit}`)} ({getUnitSymbol(category, unit)})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Box>

          {/* Swap Button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              my: 2,
            }}
          >
            <IconButton
              onClick={handleSwapUnits}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  transform: 'rotate(180deg)',
                },
                transition: 'all 0.3s ease',
                width: 50,
                height: 50,
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
              }}
            >
              <SwapVertIcon />
            </IconButton>
          </Box>

          {/* To Section */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} color="text.secondary" mb={1.5}>
              {t('to')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={toValue}
                  placeholder={t('result')}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '1.125rem',
                      fontWeight: 600,
                      color: 'primary.main',
                      bgcolor: '#f8f9fa',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={7}>
                <TextField
                  fullWidth
                  select
                  variant="outlined"
                  value={toUnit}
                  onChange={handleToUnitChange}
                  SelectProps={{
                    native: false,
                  }}
                >
                  {units.map(unit => (
                    <MenuItem key={unit} value={unit}>
                      {t(`units.${unit}`)} ({getUnitSymbol(category, unit)})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Quick Reference */}
        <Box sx={{ mt: 2 }}>
          <Alert
            severity="info"
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
              border: 'none',
            }}
          >
            <Typography variant="body2" fontWeight={500} textAlign="center">
              1 {getUnitSymbol(category, fromUnit)} = {formatNumber(convertUnit(1, fromUnit, toUnit, category))}{' '}
              {getUnitSymbol(category, toUnit)}
            </Typography>
          </Alert>
        </Box>
      </Box>
    </Fade>
  );
}

export default UnitConverter;
